/**
 * Os ganchos de leitura: uma consulta por caso de uso (H02).
 *
 * ── Por que existem, se `useQuery` já é genérico ──────────────────────────
 *
 * Para a chave e a busca nascerem juntas. Uma tela que monta a própria chave
 * pode errar por um caractere, e o erro não aparece: a consulta funciona, o
 * cache enche, e a invalidação do SSE simplesmente não a alcança. O resultado é
 * uma tela que nunca atualiza sozinha e ninguém sabe por quê.
 *
 * Com o gancho, chave e busca saem do mesmo lugar, e o `chaves.ts` é o único
 * arquivo que precisa concordar com o `chavesAtingidasPor`.
 *
 * ── Nenhum gancho de ESCRITA aqui ─────────────────────────────────────────
 *
 * Comando precisa de `Idempotency-Key` (B30), de invalidação específica e, em
 * vários casos, de confirmação. Isso é decisão de tela, e nasce com ela — da
 * H04 em diante. Um `useEnviarMensagem` genérico agora seria adivinhação.
 */

import { useMemo } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  BoardDoFunil,
  ClienteDaApi,
  EntidadeDeCampo,
  SituacaoDaOrganizacao,
  TipoDePrecoDaApi,
} from '@cliqcrm/contracts';
import { criarClienteDaSessao } from './cliente';
import { chaves } from './chaves';
import { useSessao } from '@/shared/sessao/contexto';

/**
 * O cliente da API desta sessão.
 *
 * Memorizado pela revenda: recriá-lo a cada renderização criaria uma função
 * `token` nova toda vez, e a trava de renovação — que é um módulo, não um
 * objeto — continuaria funcionando, mas o cliente perderia qualquer chance de
 * ser comparado por identidade num `useEffect`.
 */
export function useApi(): ClienteDaApi | null {
  const { estado } = useSessao();
  const revenda = estado.nome === 'autenticado' ? estado.revenda : null;

  return useMemo(() => (revenda ? criarClienteDaSessao(revenda) : null), [revenda]);
}

/**
 * Consulta que só roda com sessão.
 *
 * Sem o `enabled`, toda tela dispararia a busca antes de haver token e tomaria
 * 401 na primeira renderização — três vezes, por causa do `retry`.
 */
function useConsulta<T>(
  chave: readonly unknown[],
  buscar: (api: ClienteDaApi, sinal: AbortSignal) => Promise<T>,
): UseQueryResult<T, Error> {
  const api = useApi();

  return useQuery({
    queryKey: chave,
    enabled: api !== null,
    // O `signal` vem do TanStack e é abortado quando a consulta é cancelada —
    // troca de tela, desmontagem, chave nova. É o que impede a resposta antiga
    // de chegar depois da nova e sobrescrever o conteúdo certo.
    queryFn: ({ signal }) => buscar(api!, signal),
  });
}

/** `GET /v1/inbox/conversations` — a fila de atendimento (D02). */
export function useInbox(filtro: Record<string, string | number | undefined> = {}) {
  return useConsulta(chaves.inboxFiltrada(filtro), (api, sinal) =>
    api.listarInbox({ consulta: filtro, sinal }),
  );
}

/** `GET /v1/conversations/{id}` — a conversa composta (D01). */
export function useConversa(id: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.conversa(id ?? ''),
    enabled: api !== null && id !== null,
    queryFn: ({ signal }) => api!.comporConversa({ caminho: { id: id! }, sinal: signal }),
  });
}

/**
 * `GET /v1/admin/integrations/chatwoot/channels/whatsapp` — os números ligados.
 *
 * A tela de Conexões usa isto para dizer o que JÁ está conectado, em vez de
 * mostrar só o botão de conectar — que foi o defeito que fez uma conexão bem
 * sucedida parecer falha em 02/09/2026.
 */
/** `GET .../chatwoot/channels` — todos os canais, para a tela de Conexões. */
export function useCanaisConectados() {
  return useConsulta(chaves.canaisConectados(), (api, sinal) => api.canaisConectados({ sinal }));
}

/** `GET .../chatwoot/channels/{id}/atendentes` — quem atende o canal. */
export function useAtendentesDoCanal(inbox: string) {
  return useConsulta(chaves.atendentesDoCanal(inbox), (api, sinal) =>
    api.atendentesDoCanal({ caminho: { id: inbox }, sinal }),
  );
}

export function useCanaisDeWhatsApp() {
  return useConsulta(chaves.canaisDeWhatsApp(), (api, sinal) => api.canaisDeWhatsApp({ sinal }));
}

/**
 * `GET .../channels/whatsapp/{id}/modelos` — os modelos aprovados pela Meta.
 *
 * Lidos da META a cada abertura, e não da cópia do motor: a pergunta desta tela
 * é "já aprovaram o que cadastrei agora há pouco?", e a cópia do motor é
 * sincronizada de 3 em 3 horas.
 */
export function useModelosDeMensagem(inbox: string) {
  return useConsulta(chaves.modelosDeMensagem(inbox), (api, sinal) =>
    api.modelosDeMensagem({ caminho: { id: inbox }, sinal }),
  );
}

/**
 * `GET /v1/feedbacks` — a lista do suporte da Cliqfy (#561).
 *
 * Renovada a cada três minutos, e ao voltar para a aba depois disso: cada
 * busca traz os links das imagens, que valem de quatro a cinco minutos. Mais
 * que isso deixaria o "abrir a imagem" com link vencido; mais frequente, faria
 * a tela baixar as imagens de novo à toa.
 */
export function useFeedbacks() {
  const api = useApi();
  return useQuery({
    queryKey: chaves.feedbacks(),
    enabled: api !== null,
    queryFn: ({ signal }) => api!.listarFeedbacks({ sinal: signal }),
    staleTime: 3 * 60_000,
    refetchInterval: 3 * 60_000,
    // Voltar para a aba depois de mais de 3 min rebusca: os links já podem ter
    // vencido. Antes disso o `staleTime` segura, e nada é baixado de novo.
    refetchOnWindowFocus: true,
  });
}

/**
 * `GET /v1/inbox/nao-lidas` — o número do sino.
 *
 * Consulta minúscula e frequente: ela é invalidada por todo evento de conversa
 * do fluxo. É por isso que a rota existe separada da fila — ver o cabeçalho de
 * `nao-lidas.ts` no servidor.
 */
export function useNaoLidas() {
  return useConsulta(chaves.naoLidas(), (api, sinal) => api.naoLidasDaInbox({ sinal }));
}

/**
 * `GET /v1/inbox/caixas` — as opções do filtro "Caixas" da lista (#463), e as
 * caixas que o sino observa em qualquer tela.
 *
 * `habilitada` desliga para quem não lê conversa: a rota recusaria.
 */
export function useCaixasDoAtendimento(habilitada = true) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.caixasDoAtendimento(),
    enabled: api !== null && habilitada,
    queryFn: ({ signal }) => api!.caixasDoAtendimento({ sinal: signal }),
  });
}

/**
 * `GET /v1/notificacoes` — o conteúdo dos cards de mensagem recebida (#536).
 *
 * `habilitada` desliga para quem não lê conversa: a rota recusaria, e a
 * recusa repetida a cada evento seria ruído — não há card para esse papel.
 *
 * Sem `refetchOnWindowFocus`: o fluxo avisa quando há card novo, e rebuscar a
 * cada volta à aba traria de volta o card que a pessoa acabou de dispensar,
 * no intervalo entre dispensar e o servidor confirmar.
 */
export function useNotificacoes(habilitada: boolean) {
  const api = useApi();

  return useQuery({
    queryKey: chaves.notificacoes(),
    enabled: api !== null && habilitada,
    refetchOnWindowFocus: false,
    queryFn: ({ signal }) => api!.notificacoes({ sinal: signal }),
  });
}

/**
 * `POST /v1/notificacoes/vistas` — o card saiu da tela, e não volta em
 * nenhuma aba.
 *
 * Falha em silêncio: quem dispensou um card não pediu para ser avisado de que
 * a dispensa não foi registrada. O pior caso é o card voltar na próxima
 * leitura, e ele some sozinho quando sair da janela de uma hora.
 */
export function useMarcarNotificacoesVistas() {
  const api = useApi();
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (ids: readonly string[]) => {
      if (!api) throw new Error('sem sessão');
      return api.marcarNotificacoesVistas({ corpo: { ids: [...ids] } });
    },
    onSettled: () => {
      void cache.invalidateQueries({ queryKey: chaves.notificacoes() });
    },
    onError: () => undefined,
  });
}

/**
 * `GET /v1/opportunities/{id}/notas` — o que foi combinado na negociação.
 *
 * Só busca quando há oportunidade: o diálogo de CRIAÇÃO monta o mesmo bloco de
 * notas sem id nenhum — ali as notas ainda são locais, e vão junto no pedido
 * de criar. Sem o `enabled`, essa abertura dispararia uma chamada para
 * `/v1/opportunities/undefined/notas`.
 */
/** `GET /v1/opportunities/{id}` — para abrir o card fora do funil (28/09/2026). */
export function useOportunidade(id: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: chaves.oportunidade(id ?? 'sem-oportunidade'),
    enabled: api !== null && id !== null,
    queryFn: ({ signal }) => api!.lerOportunidade({ caminho: { id: id! }, sinal: signal }),
  });
}

export function useNotasDaOportunidade(id: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: chaves.notasDaOportunidade(id ?? 'sem-oportunidade'),
    enabled: api !== null && id !== null,
    queryFn: ({ signal }) => api!.notasDaOportunidade({ caminho: { id: id! }, sinal: signal }),
  });
}

/** `GET /v1/customers/{id}/notas` — o que a equipe escreveu sobre o cliente (059). */
export function useNotasDoCliente(id: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: chaves.notasDoCliente(id ?? 'sem-cliente'),
    enabled: api !== null && id !== null,
    queryFn: ({ signal }) => api!.notasDoCliente({ caminho: { id: id! }, sinal: signal }),
  });
}

/**
 * `GET /v1/mensagens-prontas` — os textos pré-escritos da organização.
 *
 * Cache longo pelo mesmo motivo do catálogo de etiquetas: a lista muda quando
 * alguém a edita em Configurações, o que acontece algumas vezes por mês. Quem
 * edita invalida a chave na hora.
 */
export function useMensagensProntas() {
  return useConsulta(chaves.mensagensProntas(), (api, sinal) => api.mensagensProntas({ sinal }));
}

/**
 * `GET /v1/etiquetas` — o catálogo da revenda, com as cores.
 *
 * ── Por que fica muito tempo em cache ────────────────────────────────────
 *
 * Porque o catálogo muda quando alguém cria uma etiqueta, e isso acontece
 * algumas vezes por mês. Recarregá-lo a cada abertura de conversa seria uma
 * chamada ao motor por clique na fila, para servir uma lista de dez itens que
 * não mudou.
 *
 * Quem cria etiqueta invalida esta chave na hora, então a lista nova aparece
 * sem esperar o tempo passar.
 */
export function useCatalogoDeEtiquetas() {
  return useConsulta(chaves.catalogoDeEtiquetas(), (api, sinal) =>
    api.catalogoDeEtiquetas({ sinal }),
  );
}

/**
 * `GET /v1/admin/pessoas` — a equipe da revenda, com o reflexo nas engines.
 *
 * Cada linha traz DOIS eixos além do status: `vinculoNoCrm` e
 * `acessoAoAtendimento`. Eles respondem perguntas diferentes de "esta pessoa
 * faz parte da revenda?", e é a falta deles que fazia alguém entrar como
 * `ativo` sem metade do provisionamento — descoberto dias depois pelo vendedor
 * que tentou transferir uma conversa e levou recusa.
 */
export function usePessoas() {
  return useConsulta(chaves.pessoas(), (api, sinal) => api.listarPessoas({ sinal }));
}

/**
 * `GET /v1/admin/limites` — as vagas do contrato, para a tela de Pessoas (#768).
 *
 * `habilitado` porque só o administrador da organização e o Super Admin veem
 * os números do contrato (decisão de 28/10/2026): para os outros, a rota
 * responde 403, e perguntar seria uma recusa a cada vez que a tela abre.
 */
export function useLimitesDaOrganizacao(habilitado: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.limitesDaOrganizacao(),
    enabled: api !== null && habilitado,
    queryFn: ({ signal }) => api!.limitesDaOrganizacao({ sinal: signal }),
  });
}

/**
 * `GET /v1/admin/pessoas/{id}/carteira` — quantos negócios em aberto ela tem.
 *
 * `habilitado` porque só o diálogo de redistribuir precisa disso: a lista de
 * pessoas tem trinta linhas, e perguntar por cada uma seriam trinta voltas
 * para um número que ninguém pediu.
 *
 * Este número volta como `esperado` na hora de mover, e é a trava de
 * concorrência: se alguém criar mais dois negócios entre a leitura e o clique,
 * o servidor recusa em vez de mover uma quantidade diferente da que a pessoa
 * viu.
 */
export function useCarteiraDaPessoa(usuarioId: string | null, habilitado: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.carteiraDaPessoa(usuarioId ?? ''),
    enabled: api !== null && usuarioId !== null && habilitado,
    queryFn: ({ signal }) => api!.carteiraDaPessoa({ caminho: { id: usuarioId! }, sinal: signal }),
  });
}

/** As unidades da revenda — o formulário de convite precisa delas. */
export function useUnidades() {
  return useConsulta(chaves.unidades(), (api, sinal) => api.listarUnidades({ sinal }));
}

/** Os times da revenda. Um time pertence a uma unidade; o formulário respeita. */
export function useTimes() {
  return useConsulta(chaves.times(), (api, sinal) => api.listarTimes({ sinal }));
}

/**
 * `GET /v1/conversations/{id}/responsaveis` — para quem a conversa pode ir.
 *
 * `habilitado` porque a lista só é buscada quando o diálogo abre: ela custa
 * uma volta ao motor por membro da revenda, e ninguém precisa disso ao só
 * abrir uma conversa.
 */
/**
 * `GET /v1/conversations/{id}/mencionaveis` — a lista do "@" da nota interna
 * (#569). Só busca quando o "@" abre: a maioria das notas não chama ninguém.
 */
export function useMencionaveis(conversaId: string | null, habilitado: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.mencionaveis(conversaId ?? ''),
    enabled: api !== null && conversaId !== null && habilitado,
    staleTime: 60_000,
    queryFn: ({ signal }) => api!.mencionaveis({ caminho: { id: conversaId! }, sinal: signal }),
  });
}

/** `GET /v1/notificacoes/mencoes` — "Menções para mim" (#569). */
export function useMencoesParaMim(habilitada: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.mencoesParaMim(),
    enabled: api !== null && habilitada,
    queryFn: ({ signal }) => api!.mencoesParaMim({ sinal: signal }),
  });
}

export function useResponsaveisPossiveis(id: string | null, habilitado: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.responsaveisDaConversa(id ?? ''),
    enabled: api !== null && id !== null && habilitado,
    queryFn: ({ signal }) => api!.responsaveisPossiveis({ caminho: { id: id! }, sinal: signal }),
  });
}

/**
 * `GET /v1/opportunities/{id}/responsaveis` — quem pode receber o card.
 *
 * Gêmea da de cima, e com chave de cache PRÓPRIA: as duas devolvem a mesma
 * equipe, e o que difere é o `atual` — na conversa é quem atende, no card é
 * quem responde pelo negócio. Compartilhar a chave faria uma tela mostrar a
 * marca da outra.
 */
export function useResponsaveisDaOportunidade(id: string | null, habilitado: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.responsaveisDaOportunidade(id ?? ''),
    enabled: api !== null && id !== null && habilitado,
    queryFn: ({ signal }) =>
      api!.responsaveisDaOportunidade({ caminho: { id: id! }, sinal: signal }),
  });
}

/** `GET /v1/customers/{id}/360` — a visão composta (D03). */
/**
 * `GET /v1/customers/{id}/ficha-do-paciente` (#811).
 *
 * Só pede com a clínica ligada: sem ela a rota responde 404, e pedir mesmo
 * assim encheria o console de erro em toda organização que não é clínica.
 */
export function useFichaDoPaciente(id: string, ligada: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.fichaDoPaciente(id),
    enabled: api !== null && ligada,
    queryFn: ({ signal }) => api!.fichaDoPaciente({ caminho: { id }, sinal: signal }),
  });
}

/**
 * `GET /v1/customers/{id}/paciente` (#1024): o vínculo com o paciente do SISAC e
 * o que o SISAC tem dele agora. Só montada com a clínica e a agenda ligadas.
 */
export function usePacienteDoCliente(id: string, ligada: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.pacienteDoCliente(id),
    enabled: api !== null && ligada,
    queryFn: ({ signal }) => api!.pacienteDoCliente({ caminho: { id }, sinal: signal }),
  });
}

/**
 * `GET /v1/customers/{id}/paciente/ultimas-vezes`: os atendimentos do vinculado
 * no SISAC. Só montada quando a pessoa abre a lista.
 *
 * Nada fica guardado: procedimentos são dado de saúde, e cada abertura é uma
 * leitura nova no SISAC, que entra no registro de acesso.
 */
export function useUltimasVezesDoPaciente(id: string, ligada: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.ultimasVezesDoPaciente(id),
    enabled: api !== null && ligada,
    gcTime: 0,
    staleTime: 0,
    queryFn: ({ signal }) => api!.ultimasVezesDoPaciente({ caminho: { id }, sinal: signal }),
  });
}

/** `GET /v1/opportunities/{id}/consulta` (#811): só montada com a clínica ligada. */
export function useConsultaDaOportunidade(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.consultaDaOportunidade(id),
    enabled: api !== null,
    queryFn: ({ signal }) => api!.consultaDaOportunidade({ caminho: { id }, sinal: signal }),
  });
}

/** `GET /v1/consultas/opcoes`: os médicos e as especialidades que o SISAC mantém. */
export function useOpcoesDaConsulta(ligada: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.opcoesDaConsulta(),
    enabled: api !== null && ligada,
    // A lista muda uma vez por dia, na sincronização: não precisa reler a cada aba.
    staleTime: 5 * 60_000,
    queryFn: ({ signal }) => api!.opcoesDaConsulta({ sinal: signal }),
  });
}

/**
 * `GET /v1/consultas/precos` (#1084): os preços particulares da clínica, de 20 em 20.
 *
 * Só montada com os preços ligados no mapa da agenda. A lista muda uma vez por
 * dia, na madrugada; a resposta anterior fica à vista enquanto a busca nova
 * chega, para a lista não piscar a cada letra.
 */
export function usePrecosDaClinica(
  filtro: {
    readonly tipo: TipoDePrecoDaApi | null;
    readonly busca: string;
    readonly pagina: number;
  },
  ligada: boolean,
) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.precosDaClinica({
      tipo: filtro.tipo ?? '',
      busca: filtro.busca,
      pagina: filtro.pagina,
    }),
    enabled: api !== null && ligada,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      api!.listarPrecosDaClinica({
        consulta: {
          ...(filtro.tipo ? { tipo: filtro.tipo } : {}),
          ...(filtro.busca ? { busca: filtro.busca } : {}),
          ...(filtro.pagina > 1 ? { pagina: String(filtro.pagina) } : {}),
        },
        sinal: signal,
      }),
  });
}

/**
 * `GET /v1/consultas/medicos/{id}/preco` (#1100): o preço da consulta com o
 * médico escolhido no agendamento, ou o padrão da clínica. Só com os preços
 * ligados e um médico escolhido.
 */
export function usePrecoDaConsultaDoMedico(medicoId: string, ligada: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.precoDaConsultaDoMedico(medicoId),
    enabled: api !== null && ligada && medicoId !== '',
    staleTime: 5 * 60_000,
    queryFn: ({ signal }) =>
      api!.precoDaConsultaDoMedico({ caminho: { id: medicoId }, sinal: signal }),
  });
}

/**
 * `GET /v1/customers/{id}/convenios` (#811): só montada com a clínica ligada.
 *
 * `pedir: false` só lê o que já está no cache, sem ir ao servidor: é o modo do
 * cabeçalho do 360, que mostra o convênio sem gerar uma leitura própria no
 * registro de acesso.
 */
export function useConveniosDoPaciente(id: string, pedir = true) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.conveniosDoPaciente(id),
    enabled: api !== null && pedir,
    queryFn: ({ signal }) => api!.conveniosDoPaciente({ caminho: { id }, sinal: signal }),
  });
}

/** `GET /v1/operadoras`: as manuais e as do SISAC, ativas e inativas. */
export function useOperadoras(ligada: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.operadoras(),
    enabled: api !== null && ligada,
    queryFn: ({ signal }) => api!.operadoras({ sinal: signal }),
  });
}

/**
 * `GET /v1/customers/{id}/documentos-do-paciente` (#811): só montada com a
 * clínica ligada. Relida a cada quatro minutos: o link de abrir vale cinco.
 */
export function useDocumentosDoPaciente(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.documentosDoPaciente(id),
    enabled: api !== null,
    refetchInterval: 4 * 60 * 1000,
    queryFn: ({ signal }) => api!.documentosDoPaciente({ caminho: { id }, sinal: signal }),
  });
}

/** `GET /v1/customers/{id}/exames` (#811): só montada com a clínica ligada. */
export function useExamesDoPaciente(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.examesDoPaciente(id),
    enabled: api !== null,
    queryFn: ({ signal }) => api!.examesDoPaciente({ caminho: { id }, sinal: signal }),
  });
}

/** `GET /v1/etapas-de-exame`: a lista da clínica, ativas e inativas, na ordem dela. */
export function useEtapasDaClinica(ligada: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.etapasDaClinica(),
    enabled: api !== null && ligada,
    queryFn: ({ signal }) => api!.etapasDaClinica({ sinal: signal }),
  });
}

/**
 * `GET /v1/campos-personalizados?entidade=…` (#568): os campos de um cadastro,
 * arquivados também, na ordem do formulário. Pedido de exame e tratamento só
 * com a clínica ligada: sem ela, a rota responde 404, então nem se pergunta.
 */
export function useCamposPersonalizados(entidade: EntidadeDeCampo, ligada = true) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.camposPersonalizados(entidade),
    enabled: api !== null && ligada,
    queryFn: ({ signal }) => api!.camposPersonalizados({ consulta: { entidade }, sinal: signal }),
  });
}

/** `GET /v1/customers/{id}/tratamentos` (#811): só montada com a clínica ligada. */
export function useTratamentosDoPaciente(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.tratamentosDoPaciente(id),
    enabled: api !== null,
    queryFn: ({ signal }) => api!.tratamentosDoPaciente({ caminho: { id }, sinal: signal }),
  });
}

/** `GET /v1/tratamentos`: o catálogo do SISAC, ativos e inativos. Muda na sincronização. */
export function useCatalogoDeTratamentos(ligada: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.catalogoDeTratamentos(),
    enabled: api !== null && ligada,
    staleTime: 5 * 60_000,
    queryFn: ({ signal }) => api!.catalogoDeTratamentos({ sinal: signal }),
  });
}

/**
 * `GET /v1/customers/{id}/prontuario` (ADR-0022): só montada com a clínica
 * ligada e para quem tem a marca de acesso.
 */
export function useProntuarioDoPaciente(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.prontuarioDoPaciente(id),
    enabled: api !== null,
    queryFn: ({ signal }) => api!.prontuarioDoPaciente({ caminho: { id }, sinal: signal }),
  });
}

export function useCliente360(id: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.cliente360(id ?? ''),
    enabled: api !== null && id !== null,
    queryFn: ({ signal }) => api!.cliente360({ caminho: { id: id! }, sinal: signal }),
  });
}

/**
 * `GET /v1/entrada/organizacoes` — onde esta pessoa entra.
 *
 * Serve o menu: "trocar de organização" só faz sentido para quem tem mais de
 * uma, e a alternativa seria mostrar o item sempre e descobrir no clique.
 *
 * A consulta não é por tenant — ela atravessa a escolha —, e por isso a chave
 * fica fora da árvore do tenant atual.
 */
export function useOrganizacoesDaPessoa() {
  return useConsulta(chaves.organizacoesDaPessoa(), (api, sinal) =>
    api.organizacoesDaPessoa({ sinal }),
  );
}

/**
 * `GET /v1/funis` — os funis da organização, para o quadro escolher um.
 *
 * Quem lê oportunidade lê esta lista: escolher o funil é de quem trabalha
 * nele, não de quem administra. Amarrá-la à permissão de configuração
 * deixaria o vendedor sem o seletor da própria tela.
 */
export function useFunis() {
  return useConsulta(chaves.funis(), (api, sinal) => api.funisDaOrganizacao({ sinal }));
}

/**
 * `GET /v1/pipelines/{id}/board` — as colunas do funil (D04).
 *
 * O filtro entra na CHAVE do cache, e não só na consulta: sem isso o board
 * filtrado e o board inteiro compartilhariam a mesma entrada, e alternar o
 * filtro mostraria o resultado anterior por um quadro.
 */
export function useBoardDoFunil(
  pipelineId: string | null = 'padrao',
  filtro: Record<string, string> = {},
) {
  const api = useApi();
  const chave =
    Object.keys(filtro).length === 0
      ? chaves.boardDoFunil(pipelineId ?? '')
      : [...chaves.boardDoFunil(pipelineId ?? ''), filtro];

  return useQuery({
    queryKey: chave,
    /*
     * `null` é "a lista de funis ainda não chegou", e aí a consulta NÃO sai.
     *
     * Qualquer chave nesse momento seria palpite — e o palpite errado gasta uma
     * carga inteira do quadro para receber 404, na revenda que não tem funil
     * `padrao` porque moveu tudo para funis nomeados.
     */
    enabled: api !== null && pipelineId !== null,
    queryFn: ({ signal }) =>
      api!.boardDoFunil({ caminho: { id: pipelineId! }, consulta: filtro, sinal: signal }),
    /*
     * Buscar ou filtrar troca a chave, e a chave nova nasce sem dados. Sem o
     * quadro anterior enquanto a nova chega, a tela inteira virava o esqueleto
     * a cada letra, e o campo de busca saía da tela com o foco: entrava uma
     * letra só. Só no MESMO funil: trocar de funil não mostra o quadro do outro.
     */
    placeholderData: (anterior, consulta) =>
      consulta?.queryKey[1] === (pipelineId ?? '') ? keepPreviousData(anterior) : undefined,
  });
}

/**
 * `GET /v1/pipelines/{id}/atendimentos` — o que cada card é no SISAC (#1151).
 *
 * Sai depois do quadro, e só na clínica: os detalhes enfeitam o card, e o
 * quadro não espera por eles. A chave leva só QUAIS cards estão no quadro:
 * mover um card não lê o SISAC de novo (cada leitura é uma consulta lá e um
 * registro de acesso por cliente). `board` é o quadro já do filtro pedido, e
 * não o anterior que fica na tela enquanto o novo chega. Sem a parte ligada, ou com o SISAC fora, a
 * resposta vem vazia e os cards ficam como estão.
 */
export function useAtendimentosDoFunil(
  pipelineId: string | null,
  filtro: Record<string, string>,
  board: BoardDoFunil | undefined,
  ligado: boolean,
) {
  const api = useApi();
  const assinatura = board
    ? board.colunas
        .flatMap((coluna) => coluna.cards.map((card) => card.referencia_externa))
        .sort()
        .join(',')
    : '';
  return useQuery({
    queryKey: chaves.atendimentosDoFunil(pipelineId ?? '', filtro, assinatura),
    enabled: api !== null && pipelineId !== null && ligado && assinatura !== '',
    queryFn: ({ signal }) =>
      api!.atendimentosDoFunil({ caminho: { id: pipelineId! }, consulta: filtro, sinal: signal }),
    // O SISAC muda por fora: um minuto de folga, e a próxima mudança do quadro lê de novo.
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

/** `GET /v1/opportunities/{id}/atendimentos` — os passos no SISAC, com o total (#1151). */
export function useAtendimentosDaOportunidade(oportunidadeId: string | null, ligado: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.atendimentosDaOportunidade(oportunidadeId ?? ''),
    enabled: api !== null && oportunidadeId !== null && ligado,
    queryFn: ({ signal }) =>
      api!.atendimentosDaOportunidade({ caminho: { id: oportunidadeId! }, sinal: signal }),
    staleTime: 60_000,
  });
}

/** `GET /v1/tasks` — as tarefas do escopo (D05). */
export function useTarefas(filtro: Record<string, string | number | undefined> = {}) {
  return useConsulta(chaves.tarefasFiltradas(filtro), (api, sinal) =>
    api.listarTarefas({ consulta: filtro, sinal }),
  );
}

/**
 * `GET /v1/tasks/{id}` — a tarefa inteira, com o corpo (H10).
 *
 * Servida da port e não da projeção: `read.tasks` não guarda o corpo, e é ele
 * que responde "o que exatamente eu combinei de fazer". Por isso a consulta é
 * por tarefa ABERTA, e não parte da listagem — cada uma é uma ida ao motor.
 */
export function useTarefa(id: string) {
  return useConsulta(chaves.tarefa(id), (api, sinal) => api.lerTarefa({ caminho: { id }, sinal }));
}

/**
 * `GET /v1/tasks/responsaveis` — a quem se pode atribuir (H10).
 *
 * Sem `staleTime` próprio, e é de propósito: o padrão do provedor é zero, e a
 * razão dele vale aqui inteira. A consulta só monta quando o diálogo abre, e é
 * exatamente aí que uma lista velha custaria caro — a pessoa contratada ontem
 * não apareceria, e quem foi desligado continuaria oferecido.
 */
export function useResponsaveisDeTarefa() {
  return useConsulta(chaves.responsaveisDeTarefa(), (api, sinal) =>
    api.listarResponsaveisDeTarefa({ sinal }),
  );
}

/**
 * `GET /v1/dashboard` — os indicadores da revenda (D07).
 *
 * `staleTime` alto porque o dado do outro lado é agregado por job: refazer a
 * consulta ao voltar para a aba devolveria o mesmo número. Cinco minutos é
 * metade do intervalo do job, então a tela nunca fica mais de um ciclo atrás
 * sem que a própria marca de frescor conte isso ao gestor.
 */
export function useDashboard(periodo: Record<string, string | undefined> = {}) {
  return useConsulta(chaves.dashboard(periodo), (api, sinal) =>
    api.lerDashboard({ consulta: periodo, sinal }),
  );
}

/**
 * `GET /v1/relatorios/ranking-de-vendas` — quem vendeu mais no período (#557).
 *
 * Como o dashboard, não entra nas chaves que a notificação invalida: o gestor
 * consulta o ranking por minutos, e refazê-lo a cada mensagem que chega só
 * custaria banco.
 */
export function useRankingDeVendas(filtro: Record<string, string | undefined> = {}) {
  return useConsulta(chaves.rankingDeVendas(filtro), (api, sinal) =>
    api.lerRankingDeVendas({ consulta: filtro, sinal }),
  );
}

/** `GET /v1/relatorios/ranking-de-vendas/vendas` — as vendas de uma pessoa do ranking. */
export function useVendasDoRanking(
  filtro: Record<string, string | undefined> & { responsavel: string },
) {
  return useConsulta(chaves.vendasDoRanking(filtro), (api, sinal) =>
    api.lerVendasDoRanking({ consulta: filtro, sinal }),
  );
}

/** `GET /v1/relatorios/comparativos` — o período contra a base escolhida (#557). */
export function useComparativos(filtro: Record<string, string | undefined> = {}) {
  return useConsulta(chaves.relatorio('comparativos', filtro), (api, sinal) =>
    api.lerComparativos({ consulta: filtro, sinal }),
  );
}

/** `GET /v1/relatorios/gargalos` — onde as oportunidades param no funil (#557). */
export function useGargalosDoFunil(filtro: Record<string, string | undefined> = {}) {
  return useConsulta(chaves.relatorio('gargalos', filtro), (api, sinal) =>
    api.lerGargalosDoFunil({ consulta: filtro, sinal }),
  );
}

/** `GET /v1/relatorios/gargalos/paradas` — as oportunidades paradas de um número. */
export function useOportunidadesParadas(filtro: Record<string, string | undefined> = {}) {
  return useConsulta(chaves.relatorio('paradas', filtro), (api, sinal) =>
    api.lerOportunidadesParadas({ consulta: filtro, sinal }),
  );
}

/** `GET /v1/relatorios/atendimento` — volume por canal, por hora e por pessoa (#557). */
export function useAtendimentoDoPeriodo(filtro: Record<string, string | undefined> = {}) {
  return useConsulta(chaves.relatorio('atendimento', filtro), (api, sinal) =>
    api.lerAtendimentoDoPeriodo({ consulta: filtro, sinal }),
  );
}

/** `GET /v1/identity/merge-candidates` — a fila do degrau 6 (D09). */
export function useCandidatosDeMerge(filtro: Record<string, string | undefined> = {}) {
  return useConsulta(chaves.candidatosDeMerge(filtro), (api, sinal) =>
    api.listarCandidatosDeMerge({ consulta: filtro, sinal }),
  );
}

/** `GET /v1/identity/merge-candidates/{id}` — a comparação lado a lado (D09). */
export function useCandidatoDeMerge(id: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.candidatoDeMerge(id ?? ''),
    enabled: api !== null && id !== null,
    queryFn: ({ signal }) => api!.detalharCandidatoDeMerge({ caminho: { id: id! }, sinal: signal }),
  });
}

/** `GET /v1/identity/metrics` — a taxa de vínculo automático (D09, §14.2). */
export function useMetricaDeIdentidade() {
  return useConsulta(chaves.metricaDeIdentidade(), (api, sinal) => api.medirIdentidade({ sinal }));
}

/** `GET /v1/timeline` — a linha do tempo materializada (D06). */
export function useTimeline(filtro: Record<string, string | number | undefined> = {}) {
  return useConsulta(chaves.timelineDe(filtro), (api, sinal) =>
    api.listarTimeline({ consulta: filtro, sinal }),
  );
}

// ── Administração (H13) ──────────────────────────────────────────────────

/** `GET /v1/admin/settings` — a configuração da revenda (D13). */
export function useConfiguracao() {
  return useConsulta(chaves.configuracao(), (api, sinal) => api.lerConfiguracao({ sinal }));
}

/**
 * `GET /v1/me/security` — a conta tem segundo fator? (B15)
 *
 * Vem do provedor de identidade, e não do token. O `amr` diz COMO a pessoa
 * entrou nesta sessão: quem cadastra o segundo fator com a sessão aberta
 * continua com `amr: ['pwd']` até sair e entrar de novo — e a tela, lendo dali,
 * seguiria avisando que falta cadastrar depois de já existir.
 *
 * Foi o que aconteceu: a pessoa cadastrou, o aviso continuou, ela cadastrou de
 * novo achando que não tinha pegado, e ficou com duas credenciais na conta.
 */
export function useSegurancaDaConta() {
  return useConsulta(chaves.segurancaDaConta(), (api, sinal) => api.segurancaDaConta({ sinal }));
}

/** `GET /v1/lista-de-espera/automacoes` — se a pessoa já pediu as automações (066). */
export function useLugarNaListaDasAutomacoes() {
  return useConsulta(chaves.lugarNaListaDasAutomacoes(), (api, sinal) =>
    api.lugarNaListaDasAutomacoes({ sinal }),
  );
}

/**
 * `GET /v1/operacao/lista-de-espera/automacoes` — quem pediu, em todas as
 * organizações. Só pergunta quando `ligada`: para qualquer outro papel a
 * resposta seria uma recusa, e a tela nem mostra o bloco.
 */
export function useListaDeEsperaDasAutomacoes(ligada: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.listaDeEsperaDasAutomacoes(),
    enabled: api !== null && ligada,
    queryFn: ({ signal }) => api!.listaDeEsperaDasAutomacoes({ sinal: signal }),
  });
}

/** `GET /v1/operacao/super-admins` — quem é Super Admin (ADR-0025). Só para Super Admin. */
export function useSuperAdmins() {
  return useConsulta(chaves.superAdmins(), (api, sinal) => api.superAdmins({ sinal }));
}

/** `GET /v1/operacao/planos` — o catálogo de planos da gestão (#766). Só para Super Admin. */
export function usePlanos() {
  return useConsulta(chaves.planos(), (api, sinal) => api.planos({ sinal }));
}

/**
 * `GET /v1/operacao/organizacoes` — todas as organizações, para a gestão (#767).
 * Só para Super Admin. Enquanto a busca nova não volta, a lista anterior fica
 * na tela: trocar o termo não pisca a tabela inteira para "carregando".
 */
export function useOrganizacoesNaGestao(busca: string, situacao: SituacaoDaOrganizacao | null) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.organizacoesNaGestaoFiltradas(busca, situacao),
    enabled: api !== null,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      api!.organizacoesNaGestao({
        consulta: { busca: busca || undefined, situacao: situacao ?? undefined },
        sinal: signal,
      }),
  });
}

/** `GET /v1/operacao/organizacoes/{id}` — uma organização na gestão. */
export function useOrganizacaoNaGestao(id: string) {
  return useConsulta(chaves.organizacaoNaGestao(id), (api, sinal) =>
    api.organizacaoNaGestao({ caminho: { id }, sinal }),
  );
}

/** `GET /v1/operacao/organizacoes/{id}/historico` — o histórico da organização. */
export function useHistoricoDaOrganizacao(id: string) {
  return useConsulta(chaves.historicoDaOrganizacao(id), (api, sinal) =>
    api.historicoDaOrganizacao({ caminho: { id }, sinal }),
  );
}

/**
 * `GET /v1/automacoes` — as automações da organização. Sem `situacao`, as que
 * estão em uso (todas menos as arquivadas); com `'arquivada'`, só elas.
 */
export function useAutomacoes(situacao: 'arquivada' | null = null) {
  return useConsulta(chaves.listaDeAutomacoes(situacao), (api, sinal) =>
    api.automacoes(situacao ? { consulta: { situacao }, sinal } : { sinal }),
  );
}

/** `GET /v1/automacoes/painel` — as execuções na janela pedida (padrão 24 h). */
export function usePainelDasAutomacoes(
  janela: '1h' | '6h' | '24h' | '7d' | '30d' = '24h',
  aoVivo = false,
) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.painelDasAutomacoes(janela),
    enabled: api !== null,
    refetchInterval: aoVivo ? 15_000 : false,
    queryFn: ({ signal }) => api!.painelDasAutomacoes({ consulta: { janela }, sinal: signal }),
  });
}

/** `GET /v1/automacoes/modelos` — os modelos prontos, com o nome de cada um. */
export function useModelosDeAutomacao() {
  return useConsulta(chaves.modelosDeAutomacao(), (api, sinal) =>
    api.modelosDeAutomacao({ sinal }),
  );
}

/** `GET /v1/automacoes/blocos` — os eventos, blocos e ações que o editor oferece. */
export function useBlocosDoEditor() {
  return useConsulta(chaves.blocosDoEditor(), (api, sinal) => api.blocosDoEditor({ sinal }));
}

/** `GET /v1/automacoes/{id}` — uma automação inteira: o gatilho, o mapa e a alteração pendente. */
export function useAutomacao(id: string) {
  return useConsulta(chaves.automacao(id), (api, sinal) =>
    api.automacao({ caminho: { id }, sinal }),
  );
}

/**
 * `GET /v1/automacoes/execucoes` — o que as automações fizeram, da mais nova
 * para a mais antiga. `aoVivo` pergunta de novo a cada 15 s.
 */
export function useExecucoesDasAutomacoes(
  filtro: { automacao?: string; resultado?: string; limite: number },
  aoVivo: boolean,
) {
  const api = useApi();
  return useQuery<Awaited<ReturnType<ClienteDaApi['execucoesDasAutomacoes']>>>({
    queryKey: chaves.execucoesDasAutomacoes(filtro),
    enabled: api !== null,
    refetchInterval: aoVivo ? 15_000 : false,
    // Só o "Carregar mais" mantém a lista na tela: com outro filtro, a lista
    // antiga sob a pílula nova diria o que não é.
    placeholderData: (anterior, consulta) => {
      const antes = (consulta?.queryKey[2] ?? {}) as Record<string, unknown>;
      const mesmoFiltro =
        antes['automacao'] === filtro.automacao && antes['resultado'] === filtro.resultado;
      return mesmoFiltro ? keepPreviousData(anterior) : undefined;
    },
    queryFn: ({ signal }) => api!.execucoesDasAutomacoes({ consulta: filtro, sinal: signal }),
  });
}

/** `GET /v1/automacoes/em-espera` — o que está parado num bloco de espera agora. */
export function useAutomacoesEmEspera(aoVivo: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.automacoesEmEspera(),
    enabled: api !== null,
    refetchInterval: aoVivo ? 15_000 : false,
    queryFn: ({ signal }) => api!.automacoesEmEspera({ sinal: signal }),
  });
}

/** `GET /v1/listas` — as listas: as publicadas, e para a Cliqfy também os rascunhos. */
export function useListas() {
  return useConsulta(chaves.listas(), (api, sinal) => api.listas({ sinal }));
}

/**
 * `GET /v1/listas/pedidos` — os pedidos de lista. Só pergunta quando `ligada`:
 * é de quem pede (gestor, administrador) e de quem atende (a Cliqfy).
 */
export function usePedidosDeLista(ligada: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.pedidosDeLista(),
    enabled: api !== null && ligada,
    queryFn: ({ signal }) => api!.pedidosDeLista({ sinal: signal }),
  });
}

/** `GET /v1/integracoes-de-dados/conexoes` — os bancos dos clientes (só a Cliqfy). */
export function useConexoesDeDados() {
  return useConsulta(chaves.conexoesDeDados(), (api, sinal) => api.conexoesDeDados({ sinal }));
}

/** `GET /v1/integracoes-de-dados/conexoes/{id}/mapa` — as tabelas e colunas lidas por último. */
export function useMapaDoBanco(id: string) {
  return useConsulta(chaves.mapaDoBanco(id), (api, sinal) =>
    api.mapaDoBanco({ caminho: { id }, sinal }),
  );
}

/** `GET /v1/automacoes/{id}/historico` — as versões e as mudanças, da mais nova para a mais antiga. */
export function useHistoricoDaAutomacao(id: string) {
  return useConsulta(chaves.historicoDaAutomacao(id), (api, sinal) =>
    api.historicoDaAutomacao({ caminho: { id }, sinal }),
  );
}

/**
 * `GET /v1/integracoes-de-dados/operacoes` — as operações SQL de uma finalidade.
 * Só pergunta quando `ligada`: para quem não é da Cliqfy a resposta seria 404.
 */
export function useOperacoesDeDados(finalidade: 'consulta' | 'listagem' | 'acao', ligada: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.operacoesDeDados(finalidade),
    enabled: api !== null && ligada,
    queryFn: ({ signal }) => api!.operacoesDeDados({ consulta: { finalidade }, sinal: signal }),
  });
}

/**
 * `GET /v1/admin/clinica/agenda`: que operação atende cada parte da agenda da
 * clínica. Só a Cliqfy, e só com a clínica ligada.
 */
export function useAgendaDaClinica(ligada: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.agendaDaClinica(),
    enabled: api !== null && ligada,
    queryFn: ({ signal }) => api!.agendaDaClinica({ sinal: signal }),
  });
}

/**
 * `GET /v1/integracoes-de-dados/operacoes`, sem filtro: todas as operações SQL.
 * Só pergunta quando `ligada`, como a de uma finalidade.
 */
export function useTodasAsOperacoesDeDados(ligada: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.todasAsOperacoesDeDados(),
    enabled: api !== null && ligada,
    queryFn: ({ signal }) => api!.operacoesDeDados({ sinal: signal }),
  });
}

/** `GET /v1/integracoes-de-dados/operacoes/{id}` — uma operação, na versão atual. */
export function useOperacaoDeDados(id: string) {
  return useConsulta(chaves.operacaoDeDados(id), (api, sinal) =>
    api.operacaoDeDados({ caminho: { id }, sinal }),
  );
}

/** `GET /v1/integracoes-de-dados/operacoes/{id}/historico` — as versões e o que houve em cada uma. */
export function useHistoricoDaOperacaoDeDados(id: string) {
  return useConsulta(chaves.historicoDaOperacaoDeDados(id), (api, sinal) =>
    api.historicoDaOperacaoDeDados({ caminho: { id }, sinal }),
  );
}

/** `GET /v1/admin/flags` — o catálogo, com o que está ligado (D13). */
export function useFlags() {
  return useConsulta(chaves.flags(), (api, sinal) => api.listarFlags({ sinal }));
}

/** `GET /v1/admin/integrations` — conexões de engine, sem segredo (D11). */
export function useIntegracoes() {
  return useConsulta(chaves.integracoes(), (api, sinal) => api.listarIntegracoes({ sinal }));
}

/**
 * `GET /v1/admin/integrations/catalog` — as plataformas que o produto conhece,
 * com `ativa_no_tenant` dizendo quais ESTA revenda ligou (D11, princípio 08).
 */
export function useCatalogoDePlataformas() {
  return useConsulta(chaves.catalogoDePlataformas(), (api, sinal) =>
    api.catalogoDePlataformas({ sinal }),
  );
}

/** `GET /v1/admin/dlq` — a fila de descarte, do mais velho para o mais novo (D10). */
export function useDlq(filtro: Record<string, string | undefined> = {}) {
  return useConsulta(chaves.dlq(filtro), (api, sinal) =>
    api.listarDlq({ consulta: filtro, sinal }),
  );
}

/** `GET /v1/admin/dlq/{id}` — payload referenciado e tentativas (D10). */
export function useItemDaDlq(id: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.itemDaDlq(id ?? ''),
    enabled: api !== null && id !== null,
    queryFn: ({ signal }) => api!.detalharItemDaDlq({ caminho: { id: id! }, sinal: signal }),
  });
}
